import { memo, useCallback, useEffect, useRef, useState } from 'react'
import { Popup, SlideUpHeaderBackButton } from 'common-components'
import SlideUp from 'common-components/SlideUp'
import ActionsOverlay from 'common-components/ActionsOverlay'
import AISkeletonItem from 'components/AISkeletonItem'
import { LoadingGradient } from 'common-components/LoadingGradient'
import { useGenerationProgress } from 'hooks/useGenerationProgress'
import { SMALL_DESKTOP_SIZE_BREAKPOINT } from 'consts'
import { CONTEXT_MENU_OFFSET } from 'containers/SubSidebar/AiLibContent/consts'
import { useScrollableElement } from 'containers/SubSidebar/AIVideoContent/context'
import { useResponsive } from 'hooks/useResponsive'
import { useSelector } from 'react-redux'
import { aiImageSelectors } from 'store/features/aiImage/selectors'
import { IAIImageProject } from 'store/types/aiImage'
import { normalizeAspectRatio } from 'utils/aspectRatio'
import { useAiLibImageActions } from '../hooks/useAiLibImageActions'
import ImageDetailsContent from './ImageDetailsContent'
import PreviewActionButtons from './PreviewActionButtons'
import * as Styled from './styled'
import { PopupNavArrows, SlideUpNavArrows } from '../../NavArrows'
import PreviewIcon from 'assets/svgr-icons/Preview.svg'
import { useSidebarItemDrag } from 'hooks/useSidebarItemDrag'
import { convertAiImgToBaseMediaItem } from 'containers/SubSidebar/AiImageContent/HistoryTab/Content/Image/utils'

interface IImageItemProps {
  item: IAIImageProject
  index: number
  isPreviewOpen: boolean
  onOpenPreview: (index: number) => void
  onClosePreview: () => void
  onNext: () => void
  onPrev: () => void
  hasNext: boolean
  hasPrev: boolean
}

const ImageItem = memo(function ImageItem({
  item,
  index,
  isPreviewOpen,
  onOpenPreview,
  onClosePreview,
  onNext,
  onPrev,
  hasNext,
  hasPrev
}: IImageItemProps) {
  const [isImageLoaded, setIsImageLoaded] = useState(false)
  const [isPreviewImageLoaded, setIsPreviewImageLoaded] = useState(false)
  const imageRef = useRef<HTMLImageElement>(null)
  const { scrollableElementRef } = useScrollableElement()
  const isMobile = useResponsive(SMALL_DESKTOP_SIZE_BREAKPOINT)
  const aiImageModels = useSelector(aiImageSelectors.modelsData)
  const aiImageStyles = useSelector(aiImageSelectors.stylesData)
  const image = item.images[0]
  const aspectRatio = normalizeAspectRatio(item.aspectRatio)
  const imagePath = image?.thumbnailPath || image?.filePath
  const fullSizePath = image?.filePath || imagePath
  const isProcessing = item.status === 'processing'
  const { progress } = useGenerationProgress({
    assetId: item.id,
    isGenerating: isProcessing
  })

  useEffect(() => {
    if (isPreviewOpen) {
      setIsPreviewImageLoaded(false)
    }
  }, [isPreviewOpen, fullSizePath])

  const openPreview = useCallback(
    () => onOpenPreview(index),
    [onOpenPreview, index]
  )
  const closePreview = onClosePreview

  const {
    handleAddToTimeline,
    handleCreateSimilar,
    handleDownload,
    isAiGenerated,
    ...actionsData
  } = useAiLibImageActions({
    imageProject: item,
    onClosePreview: closePreview
  })

  const handleItemClick = useCallback(() => {
    if (isPreviewOpen) {
      return
    }
    handleAddToTimeline()
  }, [isPreviewOpen, handleAddToTimeline])
  const actionButtonProps = {
    onRegenerate: handleCreateSimilar,
    onDownload: handleDownload,
    onAddToTimeline: handleAddToTimeline,
    showRegenerate: isAiGenerated
  }

  const { setNodeRef, listeners, attributes, style } = useSidebarItemDrag(
    isProcessing || !imagePath ? null : convertAiImgToBaseMediaItem(item)
  )

  if (isProcessing) {
    return (
      <Styled.Item>
        <AISkeletonItem />
        <Styled.ProcessingOverlay>
          <LoadingGradient progress={progress} size="small" />
        </Styled.ProcessingOverlay>
      </Styled.Item>
    )
  }

  if (!imagePath) {
    return null
  }

  return (
    <>
      <Styled.Item
        ref={setNodeRef}
        onClick={handleItemClick}
        style={style}
        {...listeners}
        {...attributes}
      >
        {!isImageLoaded && (
          <Styled.SkeletonOverlay>
            <AISkeletonItem />
          </Styled.SkeletonOverlay>
        )}

        <Styled.PreviewImage
          ref={imageRef}
          src={imagePath}
          onLoad={() => setIsImageLoaded(true)}
          onError={() => setIsImageLoaded(true)}
          loading="lazy"
        />

        {isImageLoaded && (
          <>
            <ActionsOverlay
              actions={actionsData}
              scrollableElementRef={scrollableElementRef}
              contextMenuPosition={{
                flyoutOptions: {
                  position: 'bottom',
                  alignmentX: isMobile ? 'right' : 'left',
                  extraOffset: CONTEXT_MENU_OFFSET
                },
                togglerSize: { width: 20, height: 20 },
                isMobileWithDrawer: true,
                showMobileBackButton: true
              }}
            />
            <Styled.PreviewButton
              onClick={e => {
                e.stopPropagation()
                openPreview()
              }}
            >
              <PreviewIcon />
            </Styled.PreviewButton>
          </>
        )}
      </Styled.Item>
      {isMobile ? (
        <SlideUp
          isOpen={isPreviewOpen}
          onClose={closePreview}
          bottomMargin={0}
          disableAnimation
          zIndex={18}
          minHeight="600px"
        >
          <SlideUpHeaderBackButton onBack={closePreview} />
          <Styled.SlideUpScrollable>
            <Styled.PreviewImageContainer $aspectRatio={aspectRatio}>
              <SlideUpNavArrows
                onPrev={onPrev}
                onNext={onNext}
                hasPrev={hasPrev}
                hasNext={hasNext}
              />
              {!isPreviewImageLoaded && (
                <Styled.PreviewSkeletonOverlay>
                  <AISkeletonItem />
                </Styled.PreviewSkeletonOverlay>
              )}
              <Styled.PreviewZoomImage
                src={fullSizePath}
                $aspectRatio={aspectRatio}
                onLoad={() => setIsPreviewImageLoaded(true)}
                loading="lazy"
              />
            </Styled.PreviewImageContainer>
            <ImageDetailsContent
              item={item}
              aiImageModels={aiImageModels}
              aiImageStyles={aiImageStyles}
            />
          </Styled.SlideUpScrollable>
          <Styled.SlideUpActions>
            <PreviewActionButtons {...actionButtonProps} />
          </Styled.SlideUpActions>
        </SlideUp>
      ) : (
        <Popup isOpen={isPreviewOpen} onClose={closePreview} disableAnimation>
          <Styled.PreviewContentWrapper>
            <PopupNavArrows
              onPrev={onPrev}
              onNext={onNext}
              hasPrev={hasPrev}
              hasNext={hasNext}
            />
            <Styled.PreviewImageContainer $aspectRatio={aspectRatio}>
              {!isPreviewImageLoaded && (
                <Styled.PreviewSkeletonOverlay>
                  <AISkeletonItem />
                </Styled.PreviewSkeletonOverlay>
              )}
              <Styled.PreviewZoomImage
                src={fullSizePath}
                $aspectRatio={aspectRatio}
                onLoad={() => setIsPreviewImageLoaded(true)}
                loading="lazy"
              />
            </Styled.PreviewImageContainer>
            <Styled.PreviewContent>
              <ImageDetailsContent
                item={item}
                aiImageModels={aiImageModels}
                aiImageStyles={aiImageStyles}
              />
              <Styled.PopupActions>
                <PreviewActionButtons {...actionButtonProps} />
              </Styled.PopupActions>
            </Styled.PreviewContent>
          </Styled.PreviewContentWrapper>
        </Popup>
      )}
    </>
  )
})

export default ImageItem
